// ============================================================
// ProtectedRoute — exige user authentifié (+ admin instance si requireAdmin)
// ============================================================

import { useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { loginState } from '../auth/returnPath';
import { api } from '../api';
import { forgetServerInfo, useServerInfo } from '../hooks/useServerInfo';

export function ProtectedRoute({
  children,
  requireAdmin = false,
}: {
  children: ReactNode;
  requireAdmin?: boolean;
}): JSX.Element {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          minHeight: '100vh',
          color: 'var(--color-text-muted)',
        }}
      >
        Chargement…
      </div>
    );
  }

  // [2.13.0] La page demandée est gardée : on y revient après la connexion.
  if (!user) return <Navigate to="/login" replace state={loginState(location)} />;

  if (requireAdmin && !user.isAdmin) return <NotAdmin />;

  return <>{children}</>;
}

/**
 * Compte connecté sans droits d'administration. [2.13.0] Sur une instance pas
 * encore réclamée, la clé de privilège donnée à l'installation en fait
 * l'administrateur (POST /privilege-keys/use). La clé n'est jamais journalisée.
 */
function NotAdmin(): JSX.Element {
  const { user, logout, refresh } = useAuth();
  const { info } = useServerInfo();
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [claimed, setClaimed] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const value = key.trim();
    if (value === '') return;
    setErr(null);
    setBusy(true);
    try {
      await api.usePrivilegeKey(value);
      setKey('');
      setClaimed(true);
      forgetServerInfo();
      // Le compte devient administrateur : la console s'ouvre.
      await refresh();
    } catch (e: unknown) {
      setErr((e as Error).message || 'Clé de privilège refusée.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        textAlign: 'center',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 440 }}>
        <h1>403 — Accès refusé</h1>
        <p style={{ color: 'var(--color-text-muted)' }}>
          Ton compte n'a pas les droits d'administration sur cette instance.
        </p>

        {info?.claimed === false && (
          <form
            onSubmit={submit}
            style={{
              marginTop: 20,
              textAlign: 'left',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 10,
              padding: 20,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Ce serveur n'est pas encore réclamé
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--color-text-muted)' }}>
              Si c'est ton serveur Siverix, entre la clé de privilège affichée à son installation
              (dans sa console) : ton compte en devient l'administrateur.
            </p>
            <label style={{ display: 'block', fontSize: 12 }}>
              Clé de privilège
              <input
                type="text"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                required
                style={inputStyle}
              />
            </label>
            {err && (
              <div
                role="alert"
                style={{
                  marginTop: 10,
                  padding: '8px 12px',
                  borderRadius: 6,
                  background: 'rgba(248,81,73,.15)',
                  color: 'var(--color-error)',
                  fontSize: 13,
                }}
              >
                {err}
              </div>
            )}
            {claimed && !err && (
              <div role="status" style={{ marginTop: 10, fontSize: 13 }}>
                Clé acceptée, ouverture de la console…
              </div>
            )}
            <button type="submit" disabled={busy || key.trim() === ''} style={primaryButtonStyle}>
              {busy ? '…' : 'Utiliser la clé'}
            </button>
          </form>
        )}

        <p style={{ marginTop: 20, fontSize: 13, color: 'var(--color-text-muted)' }}>
          Connecté en tant que <strong>{user?.displayName ?? user?.username}</strong>.{' '}
          <button type="button" onClick={() => void logout()} style={linkButtonStyle}>
            Se déconnecter
          </button>
        </p>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  marginTop: '4px',
  padding: '10px 12px',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'JetBrains Mono, Consolas, monospace',
};

const primaryButtonStyle: React.CSSProperties = {
  marginTop: '14px',
  width: '100%',
  padding: '10px',
  background: 'var(--color-accent)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
};

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--color-accent)',
  cursor: 'pointer',
  fontSize: 13,
};
